/**
 * StatusReminderBell — sino de alertas no header do tenant.
 *
 * Polls /api/tenant/appointments/pending-status a cada 60s.
 *
 * Mostra:
 *   - Atendimentos que passaram do horário de fim (status ainda scheduled)
 *     → botões inline: ✅ Concluído · ❌ Não veio
 *   - Próximos 30 min (proativo)
 *
 * Fecha Princípio 12 do MANIFESTO (software invisível).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Bell, CheckCircle2, XCircle, Clock, User } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

const POLL_INTERVAL_MS = 60000; // 1 min

function formatHM(iso) {
  try {
    return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  } catch { return "--:--"; }
}

export default function StatusReminderBell() {
  const [data, setData] = useState({ pending: [], upcoming: [], counts: { pending: 0, upcoming: 0 } });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null); // id em processamento
  const prevPendingIdsRef = useRef(new Set());
  const initializedRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/tenant/appointments/pending-status");
      setData(d);
      const currIds = new Set((d.pending || []).map((x) => x.id));
      // Primeira carga: apenas registra o estado atual, NÃO dispara toasts.
      // Toasts só disparam quando um item NOVO entra em pending após a carga inicial.
      if (initializedRef.current) {
        const prev = prevPendingIdsRef.current;
        for (const id of currIds) {
          if (!prev.has(id)) {
            const item = d.pending.find((x) => x.id === id);
            if (item) {
              toast(`⏰ ${item.client_name} — ${item.service_name}`, {
                description: `Terminou às ${formatHM(item.end_at)}. Foi concluído?`,
                duration: 8000,
              });
            }
          }
        }
      }
      prevPendingIdsRef.current = currIds;
      initializedRef.current = true;
    } catch (e) {
      // silencia — sino não deve incomodar o usuário
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [load]);

  const markStatus = async (appt, newStatus) => {
    setBusy(appt.id);
    try {
      await api.put(`/tenant/appointments/${appt.id}`, { status: newStatus });
      toast.success(
        newStatus === "completed" ? "Atendimento concluído!" : "Marcado como não compareceu",
      );
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const totalAlerts = data.counts.pending + data.counts.upcoming;
  const hasPending = data.counts.pending > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="status-reminder-bell"
          aria-label="Alertas de atendimento"
          className="relative w-9 h-9 rounded-md grid place-items-center hover:bg-muted transition-colors"
        >
          <Bell className={`w-4 h-4 ${hasPending ? "text-red-500" : ""}`} strokeWidth={1.8} />
          {totalAlerts > 0 && (
            <span
              data-testid="status-reminder-badge"
              className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full text-[10px] font-bold grid place-items-center text-white ${
                hasPending ? "bg-red-500" : "bg-amber-500"
              }`}
            >
              {totalAlerts}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        side="bottom"
        className="w-96 p-0 max-h-[70vh] overflow-y-auto"
        data-testid="status-reminder-popover"
      >
        <div className="px-4 py-3 border-b border-border">
          <div className="font-display font-semibold text-sm">Alertas do atendimento</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {totalAlerts === 0 && "Tudo em dia. Sem alertas no momento."}
            {data.counts.pending > 0 && `${data.counts.pending} aguardando confirmação de status`}
            {data.counts.pending > 0 && data.counts.upcoming > 0 && " · "}
            {data.counts.upcoming > 0 && `${data.counts.upcoming} nos próximos 30 min`}
          </div>
        </div>

        {/* Pending — precisa marcar status */}
        {data.pending.length > 0 && (
          <div className="p-3 space-y-2 border-b border-border bg-red-500/5">
            <div className="text-[10px] uppercase tracking-wider text-red-700 font-semibold px-1">
              Terminou · Marcar status
            </div>
            {data.pending.map((a) => (
              <div
                key={a.id}
                data-testid={`pending-appt-${a.id}`}
                className="rounded-md border border-red-500/20 bg-card p-3 space-y-2"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5 text-muted-foreground" />
                      {a.client_name || "Cliente"}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {a.service_name} · {formatHM(a.start_at)} → {formatHM(a.end_at)}
                    </div>
                  </div>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-700 font-medium shrink-0">
                    +{a.overdue_minutes}min
                  </span>
                </div>
                <div className="flex gap-1.5">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 gap-1 h-8 text-xs border-emerald-500/40 text-emerald-700 hover:bg-emerald-500/10"
                    onClick={() => markStatus(a, "completed")}
                    disabled={busy === a.id}
                    data-testid={`pending-complete-${a.id}`}
                  >
                    <CheckCircle2 className="w-3 h-3" /> Concluído
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 gap-1 h-8 text-xs border-red-500/40 text-red-700 hover:bg-red-500/10"
                    onClick={() => markStatus(a, "no_show")}
                    disabled={busy === a.id}
                    data-testid={`pending-noshow-${a.id}`}
                  >
                    <XCircle className="w-3 h-3" /> Não veio
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Upcoming — próximos 30 min */}
        {data.upcoming.length > 0 && (
          <div className="p-3 space-y-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold px-1">
              Próximos 30 min
            </div>
            {data.upcoming.map((a) => (
              <div
                key={a.id}
                data-testid={`upcoming-appt-${a.id}`}
                className="rounded-md border border-border bg-card p-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-amber-600" />
                      {a.client_name || "Cliente"}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {a.service_name} · {formatHM(a.start_at)}
                    </div>
                  </div>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 font-medium shrink-0">
                    em {a.starts_in_minutes}min
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {totalAlerts === 0 && (
          <div className="p-8 text-center text-xs text-muted-foreground">
            🎉 Nenhum alerta agora.
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
